export type AccessibilitySelection = 'on' | 'off';
export type FocusHighlightSelection = 'on' | 'off';

export const ACCESSIBILITY_STORAGE_KEY = 'chattenger:web:accessibility';
export const DEFAULT_ACCESSIBILITY_SELECTION: AccessibilitySelection = 'on';
export const FOCUS_HIGHLIGHT_STORAGE_KEY = 'chattenger:web:focus-highlights';
export const DEFAULT_FOCUS_HIGHLIGHT_SELECTION: FocusHighlightSelection = 'on';

export const normalizeAccessibilitySelection = (value: string | null | undefined): AccessibilitySelection =>
    value === 'off' ? 'off' : DEFAULT_ACCESSIBILITY_SELECTION;

export const normalizeFocusHighlightSelection = (value: string | null | undefined): FocusHighlightSelection =>
    value === 'off' ? 'off' : DEFAULT_FOCUS_HIGHLIGHT_SELECTION;

export const resolveEffectiveFocusHighlightSelection = (
    accessibilitySelection: AccessibilitySelection,
    focusHighlightSelection: FocusHighlightSelection
): FocusHighlightSelection => (accessibilitySelection === 'off' ? 'off' : focusHighlightSelection);

export const buildAccessibilityBootstrapScript = () =>
    `
(() => {
    const accessibilityStorageKey = '${ACCESSIBILITY_STORAGE_KEY}';
    const focusStorageKey = '${FOCUS_HIGHLIGHT_STORAGE_KEY}';
    const root = document.documentElement;
    const normalizeAccessibility = (value) => (value === 'off' ? 'off' : '${DEFAULT_ACCESSIBILITY_SELECTION}');
    const normalizeFocus = (value) => (value === 'off' ? 'off' : '${DEFAULT_FOCUS_HIGHLIGHT_SELECTION}');
    const shouldStripAttribute = (name) => name === 'role' || name.startsWith('aria-');
    const stripElement = (element) => {
        for (const attribute of Array.from(element.attributes)) {
            if (shouldStripAttribute(attribute.name)) {
                element.removeAttribute(attribute.name);
            }
        }
    };
    const stripTree = (rootNode) => {
        if (!rootNode || typeof rootNode.querySelectorAll !== 'function') {
            return;
        }
        if (rootNode instanceof Element) {
            stripElement(rootNode);
        }
        for (const element of rootNode.querySelectorAll('*')) {
            stripElement(element);
        }
    };
    const stopObserver = () => {
        if (window.__chattengerAccessibilityObserver) {
            window.__chattengerAccessibilityObserver.disconnect();
            window.__chattengerAccessibilityObserver = null;
        }
    };
    const startObserver = () => {
        stopObserver();
        if (root.dataset.accessibility !== 'off') {
            return;
        }
        stripTree(document.body || document.documentElement);
        const observer = new MutationObserver((records) => {
            for (const record of records) {
                if (record.type === 'attributes') {
                    if (record.target instanceof Element) {
                        stripElement(record.target);
                    }
                    continue;
                }
                for (const node of record.addedNodes) {
                    if (node instanceof Element || node instanceof DocumentFragment) {
                        stripTree(node);
                    }
                }
            }
        });
        observer.observe(document.documentElement, {
            subtree: true,
            childList: true,
            attributes: true,
        });
        window.__chattengerAccessibilityObserver = observer;
    };

    try {
        const accessibilitySelection = normalizeAccessibility(window.localStorage.getItem(accessibilityStorageKey));
        const focusSelection = normalizeFocus(window.localStorage.getItem(focusStorageKey));
        root.dataset.accessibility = accessibilitySelection;
        root.dataset.focusHighlights = accessibilitySelection === 'off' ? 'off' : focusSelection;
        startObserver();
    } catch (_error) {
        root.dataset.accessibility = '${DEFAULT_ACCESSIBILITY_SELECTION}';
        root.dataset.focusHighlights = '${DEFAULT_FOCUS_HIGHLIGHT_SELECTION}';
        stopObserver();
    }
})();
`.trim();
